import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, GlassCard, GhostButton } from "@/components/Primitives";
import ExportButton from "@/components/ExportButton";
import { Server, Wrench, Globe, AlertTriangle } from "lucide-react";

const TYPES = [
  { id: "all", label: "All" },
  { id: "hosting", label: "Hosting", Icon: Server },
  { id: "maintenance", label: "Maintenance", Icon: Wrench },
  { id: "domain", label: "Domains", Icon: Globe },
];

const ICON_FOR = { hosting: Server, maintenance: Wrench, domain: Globe };

function urgencyTone(d) {
  if (d === null || d === undefined) return "bg-white/5 text-white/40 border-white/10";
  if (d < 0) return "bg-rose-500/15 text-rose-300 border-rose-500/30";
  if (d <= 7) return "bg-rose-400/15 text-rose-300 border-rose-400/30";
  if (d <= 30) return "bg-amber-400/15 text-amber-300 border-amber-400/30";
  if (d <= 60) return "bg-sky-400/15 text-sky-300 border-sky-400/30";
  return "bg-emerald-400/10 text-emerald-300 border-emerald-400/20";
}

export default function Renewals() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [days, setDays] = useState(90);
  const [filter, setFilter] = useState("all");

  const load = async (d = days) => {
    const { data } = await api.get("/renewals", { params: { days: d } });
    setData(data);
  };
  useEffect(() => { load(days); }, [days]); // eslint-disable-line

  const filtered = useMemo(
    () => (filter === "all" ? data.items : data.items.filter((i) => i.type === filter)),
    [filter, data.items]
  );

  const buckets = useMemo(() => {
    const b = { overdue: [], week: [], month: [], later: [] };
    data.items.forEach((it) => {
      if (it.days_left < 0) b.overdue.push(it);
      else if (it.days_left <= 7) b.week.push(it);
      else if (it.days_left <= 30) b.month.push(it);
      else b.later.push(it);
    });
    return b;
  }, [data.items]);

  const totalAmount = filtered.reduce((s, it) => s + (it.amount || 0), 0);

  return (
    <div data-testid="renewals-page">
      <PageHeader
        title="Renewal Center"
        subtitle="Every upcoming hosting, maintenance, and domain renewal — at a glance."
        testId="renewals-header"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            {[30, 60, 90, 180].map((d) => (
              <button
                key={d} onClick={() => setDays(d)} data-testid={`renewals-days-${d}`}
                className={`px-4 py-2 rounded-full text-xs font-medium ${days === d ? "bg-[#007AFF] text-white shadow-[0_0_16px_rgba(0,122,255,0.4)]" : "bg-white/5 text-white/55 hover:bg-white/10"}`}
              >{d}d</button>
            ))}
            <ExportButton endpoint="/exports/renewals" range={
              days <= 7 ? "weekly" : days <= 30 ? "monthly" : days <= 90 ? "quarterly" : days <= 180 ? "semi_annual" : "annual"
            } testId="renewals-export-btn" label="Export" />
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {[
          ["Overdue", buckets.overdue.length, "text-rose-300"],
          ["Next 7 days", buckets.week.length, "text-amber-300"],
          ["Next 30 days", buckets.month.length, "text-sky-300"],
          ["Total Amount", fmtMoney(totalAmount), "text-white"],
        ].map(([label, value, tone], i) => (
          <GlassCard key={i} testId={`renewal-summary-${i}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 flex items-center gap-1.5">
              {i === 0 && <AlertTriangle className="w-3 h-3 text-rose-400" />}
              {label}
            </div>
            <div className={`font-display text-3xl ${tone}`}>{value}</div>
          </GlassCard>
        ))}
      </div>

      <div className="flex gap-2 mb-5">
        {TYPES.map(({ id, label, Icon }) => (
          <button
            key={id} onClick={() => setFilter(id)} data-testid={`renewals-filter-${id}`}
            className={`px-4 py-2 rounded-full text-xs font-medium inline-flex items-center gap-2 ${filter === id ? "bg-[#007AFF] text-white" : "bg-white/5 text-white/55 hover:bg-white/10"}`}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((it) => {
          const Icon = ICON_FOR[it.type] || Server;
          return (
            <GlassCard key={`${it.type}-${it.id}`} className="!p-4 flex items-center gap-4 hover:border-[#007AFF]/30 transition" testId={`renewal-${it.type}-${it.id}`}>
              <div className="w-11 h-11 rounded-2xl bg-white/[0.05] flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#007AFF]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{it.label}</div>
                <div className="text-xs text-white/45 truncate">
                  {it.client_name && <Link to="/clients" className="hover:text-white">{it.client_name}</Link>}
                  {it.subtitle && <> · {it.subtitle}</>}
                </div>
              </div>
              <div className="text-right hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Amount</div>
                <div className="font-display text-base text-white">{fmtMoney(it.amount)}</div>
              </div>
              <div className="text-right hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Renews</div>
                <div className="text-xs text-white/65">{fmtDate(it.date)}</div>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider border ${urgencyTone(it.days_left)}`}>
                {it.days_left < 0 ? `${Math.abs(it.days_left)}d overdue` : `${it.days_left}d`}
              </span>
            </GlassCard>
          );
        })}
        {filtered.length === 0 && (
          <GlassCard className="text-center text-white/40">
            No upcoming renewals in this window. You&apos;re ahead of the curve.
          </GlassCard>
        )}
      </div>
    </div>
  );
}
